/**
 * WEB tour STEPS, keyed by catalog id ("web:<name>").
 *
 * The title / summary / roles / release date live in the SERVER catalog
 * (server/tours/catalog.js) — this file only says how to walk the tour. An id
 * the server announces but this registry doesn't know is dropped (and logged
 * once as `unknown_tour`); an id here that the server doesn't list is never
 * shown. `__tests__/tourTests/tourRegistry.test.js` keeps the two in step and
 * checks every anchor / action below exists in client/src.
 *
 * Shape (full contract: TOUR_AUTHORING_GUIDE.md):
 *
 *   "web:emails-delete": {
 *     route: "/bdr-office/email-inbox",   // start route (startMode "navigate");
 *                                          // for "here-only" tours, the page
 *                                          // pattern it can be started on
 *     steps: [
 *       {
 *         anchor: "emails-row",   // data-tour="emails-row" on the element;
 *                                 // omit for a centred card (intro / outro)
 *         title: "Pick an email",
 *         body: "Each row is one email…",
 *         media: { type: "image" | "video", src: "/tours/emails-delete-1.mp4" },
 *         route: "/bdr-office/…", // per-step route for a multi-page tour
 *         action: "emails-open-compose", // useTourAction id, run before the
 *                                        // anchor is looked up
 *         placement: "auto",      // auto | top | bottom
 *         optional: true,         // skip (don't end) when the anchor is missing
 *         modal: "create-event",  // or an array: the tagged modals this step
 *                                 // is shown inside (BdrModal tourTag /
 *                                 // useTourModal). Kept open only while a
 *                                 // step names them; closed on every other
 *                                 // step and when the tour ends.
 *       },
 *     ],
 *   },
 *
 */
const WEB_TOUR_STEPS = {
  /* No step inside TaskNoteModal: the note box's own empty state already says
     "Click to add a note", and editing this shipped tour would not re-announce
     it anyway (guide §7). */
  "web:task-note": {
    route: "/bdr-office/tasks",
    steps: [
      {
        anchor: "task-note",
        title: "Your own note",
        body:
          "This box is a note just for you. Use it for anything you want to keep in front of you: a call-back, a reminder, a number you'll need later.\n\n" +
          "Only you can see it. It's saved to your account, so it's there on any computer you sign in on.",
      },
      {
        anchor: "task-note",
        title: "Writing in it",
        body:
          "Click anywhere in the box to open it. Type, then press Save. Cancel leaves it as it was.\n\n" +
          "To clear it, delete the text and save.",
      },
    ],
  },

  /* The first tour with modal steps (`modal`, tours/tourModals.js): steps 2-3
     open the calendar and its Create Event form, and the engine closes both
     on Back to step 1, on Exit / Esc, and when the tour finishes. */
  "web:avail-sooner": {
    route: "/bdr-office/tasks",
    steps: [
      {
        anchor: "avail-sooner-list",
        title: "Avail. Sooner",
        body:
          "Customers who'd take an earlier slot. When a job cancels or a day opens up, this is your call list.\n\n" +
          "Each row is one booked visit: click the name to open the customer, the map button copies their address, and the calendar button opens the visit on the calendar. A visit drops off once its date has passed.",
      },
      {
        action: "calendar-open",
        modal: "calendar-week",
        anchor: "calendar-create-event",
        title: "Adding someone to the list",
        body: "It's done on the calendar, when you book or edit a job or go-by. The Create button opens the booking form.",
      },
      {
        action: "calendar-open-create",
        modal: ["calendar-week", "create-event"],
        anchor: "create-event-avail-sooner",
        title: "Tick Available Sooner",
        body:
          "Tick this when the customer would come in earlier.\n\n" +
          "To take someone off the list later, untick the same box on the job's edit form, or on its popup on the calendar (Avail. Sooner), where the change saves straight away.",
      },
    ],
  },

  /* Drive-time pins (registry §17.10; plan plans/plan_tour_drive_time_pins_2026_10_09.md).
     ONE-CLICK START from the Tasks page, yet it still shows a customer's page:
     step 2's action hops to the most recently viewed customer, and the three
     client-page steps are `optional` so a brand-new account with no recents
     carries on from the calendar. Step 3's route is the PATTERN of the client
     page — it is never navigated to (an action already got there); it makes
     the page count as a tour route for the navigation watcher, and when the
     hop was skipped it reads "unreachable" and the optional step is skipped.
     Every surface a step shows is DEMO state (guide §5): a parked-and-
     restored demo pin, a fixed demo visit, canned legs, a demo list row. The
     Avail. Sooner box is explained, never ticked. */
  "web:drive-time-pins": {
    route: "/bdr-office/tasks",
    steps: [
      {
        title: "Drive time between jobs",
        body:
          "When a slot opens between two visits, pin the visit before it and the one after, then ask any customer how far they are from each.\n\n" +
          "No more copying addresses into Maps one at a time.",
      },
      {
        action: "open-recent-client",
        anchor: "client-drive-time",
        optional: true,
        title: "Every customer's page has this button",
        body:
          "The route button opens a small menu: pin this customer as the start of a gap, as the end, or compare them against whatever is pinned.",
      },
      {
        route: "/bdr-office/clients/view-client/:clientId",
        action: "drive-time-demo-pin-start",
        anchor: "client-drive-time",
        optional: true,
        title: "Pin this customer as the start",
        body:
          "Pin as start marks the visit before the gap. The next step pins the visit after it.\n\n" +
          "(Done for you here as a demo. Your own pins come back when the walkthrough ends.)",
      },
      {
        action: "drive-time-demo-ensure-pin",
        anchor: "drive-time-chip",
        title: "What's pinned is always up here",
        body:
          "Start and end, with an × on each. Pins stay until you clear them or sign out, and the calendar in another window sees the same pins.",
      },
      {
        action: "calendar-open",
        modal: "calendar-week",
        anchor: "calendar-week-grid",
        title: "Find the visit on either side of the gap",
        body: "Open the calendar and click the job before or after the opening.",
      },
      {
        action: "drive-time-demo-popup",
        modal: ["calendar-week", "event-popup-demo"],
        anchor: "event-drive-time",
        title: "Every visit's popup has the same button",
        body:
          "Pin this visit as the end of the gap, or compare it against what's pinned.\n\n" +
          "(This is a demo visit for the walkthrough.)",
      },
      {
        action: "drive-time-demo-compare",
        modal: ["calendar-week", "event-popup-demo", "drive-time-compare"],
        anchor: "drive-time-legs",
        title: "The answer",
        body:
          "The route on the map, minutes and miles from the start pin, minutes and miles to the end pin, and the total.\n\n" +
          "Typical drive time without traffic.",
      },
      {
        modal: ["calendar-week", "event-popup-demo"],
        anchor: "event-avail-sooner",
        title: "Avail. Sooner",
        body:
          "If the customer would take an earlier slot, tick this on their visit. It adds them to the call list on the Tasks page. Untick it there, or on the edit form, to take them off.\n\n" +
          "(Not ticked here — a walkthrough never changes real data.)",
      },
      {
        route: "/bdr-office/tasks",
        action: "avail-sooner-demo",
        modal: "avail-sooner-demo",
        anchor: "avail-sooner-row",
        title: "The call list, with drive times",
        body:
          "While anything is pinned, every row shows how far that customer is from the start pin and to the end pin — one lookup for the whole list.\n\n" +
          "(The row shown is a demo.)",
      },
      {
        modal: "avail-sooner-demo",
        anchor: "avail-sooner-drive",
        title: "Compare or pin from the list",
        body: "The same route button on each row opens the full comparison, or pins that customer instead.",
      },
      {
        title: "That's it",
        body:
          "Clear the pins from the chip when you're done.\n\n" +
          "The demo pin is removed now, and anything you had pinned before is put back.",
      },
    ],
  },

  /* The Business Ledger (registry §20; plans/plan_business_ledger_search_tours_2026_10_23.md):
     what a row is, the year pills, the summary cards, the type chips, the month
     groups, the search across every year, then each of the other tabs by its
     tab button. Every action is a tab switch or the search demo; the page and
     all its tabs mount reads only (guide §6 table). `ledger-show-ledger` first
     returns the page to its plain year view — a user who searched before
     starting would otherwise have no pill row to highlight. The demo types a
     REAL customer from the loaded year and mounts the tagged surface
     `ledger-search-demo`, which the engine clears on every step that doesn't
     name it; the results step re-runs the action so Back from the Refinishers
     step (which closed the tag) re-opens it. Steps 4 and 6 are optional: an
     empty year has no month row, and a demo with no term has no results. The
     month and year-group anchors sit on BOTH the table row and the phone card
     header — one of the two is display:none per breakpoint. Admin only. */
  "web:business-ledger": {
    route: "/bdr-office/business-ledger",
    steps: [
      {
        title: "The Business Ledger",
        body:
          "Every approved job form becomes one row here: the job's date, the customer, who did the work, the zip, the type and the amount.\n\n" +
          "It's read-only here: to change a row, edit the job form and approve it again.",
      },
      {
        action: "ledger-show-ledger",
        anchor: "ledger-year-pills",
        title: "One year at a time",
        body:
          "Pick a year. The current year loads first; the pills go back to the oldest year on record. " +
          "A year is fetched once and kept while you're on the page.",
      },
      {
        anchor: "ledger-summary-cards",
        title: "The year at a glance",
        body:
          "Total revenue for the year, how many M, R and RM jobs, and the monthly average — the total over the " +
          "months elapsed this year, or over 12 for a past year.",
      },
      {
        anchor: "ledger-filter-chips",
        title: "Filter by job type",
        body: "M is maintenance, R refinish, RM both on one visit, HC a hardware change, Other anything else. All puts everything back.",
      },
      {
        anchor: "ledger-month-row",
        optional: true,
        title: "Grouped by month",
        body: "Rows sit under their month with the month's subtotal, newest first.",
      },
      {
        action: "ledger-search-demo",
        modal: "ledger-search-demo",
        anchor: "ledger-search",
        title: "Search every year",
        body:
          "Type at least 3 letters of a customer's or refinisher's name, or the start of a zip code, and the search " +
          "looks across every year at once — not just the one picked.\n\n" +
          "(Filled in here with a real customer as a demo; it clears when the walkthrough ends.)",
      },
      {
        action: "ledger-search-demo",
        modal: "ledger-search-demo",
        anchor: "ledger-year-group",
        optional: true,
        title: "Results by year",
        body:
          "Matches are grouped by year, newest first, with each year's count and total. The type chips still narrow them. " +
          "A very broad search shows the newest 500 and asks you to type more.",
      },
      {
        action: "ledger-show-refinishers",
        anchor: "ledger-tab-refinishers",
        title: "Refinisher Summary",
        body:
          "Each refinisher's job count and revenue for the year. A job with several refinishers splits its amount evenly " +
          "between them, so the column adds up to the year's total. Material cost and net show a dash until the server sends them.",
      },
      {
        action: "ledger-show-metrics",
        anchor: "ledger-tab-metrics",
        title: "Metrics",
        body: "The same business metrics as the Metrics page: jobs, revenue and averages by year and month, new against repeat customers.",
      },
      {
        action: "ledger-show-door-margin",
        anchor: "ledger-tab-door-margin",
        title: "Door Margin",
        body:
          "What a door costs to do against what it earns, from the material recipes and operating expenses. " +
          "Recompute refreshes the snapshot; nothing here changes on its own.",
      },
      {
        action: "ledger-show-inventory",
        anchor: "ledger-tab-inventory",
        title: "Inventory",
        body:
          "Stock movements turned into dollars for a date range: opening and closing value, purchases, what went out " +
          "to techs, losses — plus the purchases report.",
      },
      {
        action: "ledger-show-ledger",
        title: "That's the ledger",
        body: "Back on the Ledger tab. Pick a year, filter by type, or search every year from the box.",
      },
    ],
  },
};

export default WEB_TOUR_STEPS;
